import { apiRequest } from '@/shared/lib/api-client';

export interface FlowNode {
  id: string;
  type:
    | 'trigger'
    | 'message'
    | 'input'
    | 'condition'
    | 'condition_vars'
    | 'menu'
    | 'retry'
    | 'schedule_table'
    | 'transfer_human'
    | 'wait'
    | 'webhook'
    | 'end';
  position: { x: number; y: number };
  data: Record<string, unknown>;
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  label?: string;
}

export interface FlowDefinition {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export interface ChatbotRow {
  id: number;
  name: string;
  status: 'active' | 'inactive';
  trigger_type: 'any_message' | 'keyword' | 'schedule' | 'first_contact' | 'manual';
  whatsapp_number_id: number | null;
  /** Nome do 1º canal vinculado (legado) — null = sem vínculo. Só na lista. */
  linked_channel?: string | null;
  /** TODOS os canais vinculados (atende inbound). Vazio = sem vínculo. Só na lista. */
  linked_channels?: string[];
  test_phone: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface ChatbotDetail extends ChatbotRow {
  trigger_config: Record<string, unknown>;
  flow_definition: FlowDefinition;
}

export function listChatbots(): Promise<ChatbotRow[]> {
  return apiRequest<ChatbotRow[]>('/chatbots');
}

export function getChatbot(id: number): Promise<ChatbotDetail> {
  return apiRequest<ChatbotDetail>(`/chatbots/${id}`);
}

export interface CreateChatbotInput {
  name: string;
  trigger_type?: ChatbotRow['trigger_type'];
  trigger_config?: Record<string, unknown>;
  whatsapp_number_id?: number | null;
  test_phone?: string | null;
}

export function createChatbot(input: CreateChatbotInput): Promise<{ id: number }> {
  return apiRequest<{ id: number }>('/chatbots', { method: 'POST', body: input });
}

export interface UpdateChatbotInput {
  name?: string;
  trigger_type?: ChatbotRow['trigger_type'];
  trigger_config?: Record<string, unknown>;
  flow_definition?: FlowDefinition;
  whatsapp_number_id?: number | null;
  test_phone?: string | null;
}

export function updateChatbot(id: number, input: UpdateChatbotInput): Promise<{ version: number }> {
  return apiRequest<{ version: number }>(`/chatbots/${id}`, { method: 'PATCH', body: input });
}

export function activateChatbot(id: number): Promise<void> {
  return apiRequest<void>(`/chatbots/${id}/activate`, { method: 'POST', body: {} });
}

export function deactivateChatbot(id: number): Promise<void> {
  return apiRequest<void>(`/chatbots/${id}/deactivate`, { method: 'POST', body: {} });
}

export function duplicateChatbot(id: number): Promise<{ id: number }> {
  return apiRequest<{ id: number }>(`/chatbots/${id}/duplicate`, { method: 'POST', body: {} });
}

export function deleteChatbot(id: number): Promise<void> {
  return apiRequest<void>(`/chatbots/${id}`, { method: 'DELETE' });
}

export function testChatbot(id: number): Promise<{ execution_id: number }> {
  return apiRequest<{ execution_id: number }>(`/chatbots/${id}/test`, {
    method: 'POST',
    body: {},
  });
}

export interface ChatbotExecution {
  id: number;
  contact_id: number;
  ticket_id: number | null;
  current_node_id: string | null;
  status: 'running' | 'completed' | 'transferred' | 'abandoned' | 'errored';
  started_at: string;
  ended_at: string | null;
  error_message: string | null;
  node_path: string[];
  state: Record<string, unknown>;
  last_inbound: string | null;
}

export function listExecutions(
  chatbotId: number,
  filters: { status?: ChatbotExecution['status']; limit?: number } = {},
): Promise<ChatbotExecution[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return apiRequest<ChatbotExecution[]>(`/chatbots/${chatbotId}/executions${qs ? `?${qs}` : ''}`);
}

export function reprocessExecution(executionId: number): Promise<void> {
  return apiRequest<void>(`/chatbots/executions/${executionId}/reprocess`, {
    method: 'POST',
    body: {},
  });
}

export interface WhatsappNumberOption {
  id: number;
  display_number: string;
  display_name: string | null;
  channel_type: string;
  status: string;
}

export function listWhatsappNumbers(): Promise<WhatsappNumberOption[]> {
  return apiRequest<WhatsappNumberOption[]>('/whatsapp-numbers');
}
